/**
 * @fileoverview Slice Redux para gerenciamento de autenticação.
 * Gerencia estado de autenticação, login, cadastro, logout e refresh de tokens
 * usando cookies httpOnly para segurança.
 * 
 * @module AuthSlice
 * @since 1.0.0
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as authService from '../../services/authService';
import { clearCart, clearCartWithSync, loadCartAsync, saveCartAsync } from './cartSlice';
import { clearFavorites, clearFavoritesWithSync, loadFavoritesAsync, saveFavoritesAsync } from './favoritesSlice';
import { showAllProducts } from './productsSlice';

/**
 * Thunk para login do usuário com sincronização de carrinho e favoritos.
 * @async
 * @function loginAsync
 * @param {{email: string, password: string}} credentials - Credenciais do usuário
 * @returns {Promise<void>}
 */
export const loginAsync = createAsyncThunk('auth/login', async (credentials, { rejectWithValue, dispatch }) => {
  try {
    const data = await authService.login(credentials);
    console.log('🔑 Login realizado, dados recebidos:', data);
    
    // Salvar dados imediatamente
    localStorage.setItem('user', JSON.stringify(data.user));
    if (data.accessToken) {
      localStorage.setItem('accessToken', data.accessToken);
      console.log('🔑 AccessToken salvo no localStorage');
    }
    if (data.refreshToken) {
      localStorage.setItem('refreshToken', data.refreshToken);
      console.log('🔑 RefreshToken salvo no localStorage');
    }
    
    // Aguardar um pouco para garantir que o token foi salvo antes das requisições
    await new Promise(resolve => setTimeout(resolve, 50));
    
    console.log('🔄 [AUTH] Iniciando sincronização pós-login');
    console.log('🔑 [AUTH] Token no localStorage:', localStorage.getItem('accessToken') ? 'presente' : 'ausente');
    
    // Limpar dados locais antes de carregar da sessão
    dispatch(clearFavorites());
    dispatch(clearCart());
    
    // Carregar dados da sessão
    dispatch(loadFavoritesAsync());
    dispatch(loadCartAsync());
    
    // Navegar para home page
    dispatch(showAllProducts());
    
    return data.user;
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

/**
 * Thunk para cadastro do usuário com sincronização de carrinho e favoritos.
 * @async
 * @function signUpAsync
 * @param {{name: string, email: string, password: string}} userData - Dados do usuário
 * @returns {Promise<void>}
 */
export const signUpAsync = createAsyncThunk('auth/signUp', async (userData, { rejectWithValue, dispatch }) => {
  try {
    const data = await authService.signUp(userData);
    console.log('🔑 Cadastro realizado, dados recebidos:', data);
    
    // Salvar dados imediatamente
    localStorage.setItem('user', JSON.stringify(data.user));
    if (data.accessToken) {
      localStorage.setItem('accessToken', data.accessToken);
      console.log('🔑 AccessToken salvo no localStorage');
    }
    if (data.refreshToken) {
      localStorage.setItem('refreshToken', data.refreshToken);
      console.log('🔑 RefreshToken salvo no localStorage');
    }
    
    // Aguardar um pouco para garantir que o token foi salvo antes das requisições
    await new Promise(resolve => setTimeout(resolve, 50));
    
    console.log('🔄 [AUTH] Iniciando sincronização pós-cadastro');
    
    // Limpar dados locais antes de carregar da sessão
    dispatch(clearFavorites());
    dispatch(clearCart());
    
    // Carregar dados da sessão (novos usuários terão dados vazios)
    dispatch(loadFavoritesAsync());
    dispatch(loadCartAsync());
    
    // Navegar para home page
    dispatch(showAllProducts());
    
    return data.user;
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

/**
 * Thunk para verificar token ao inicializar app.
 * @async
 * @function verifyTokenAsync
 * @returns {Promise<void>}
 */
export const verifyTokenAsync = createAsyncThunk('auth/verifyToken', async (_, { rejectWithValue }) => {
  try {
    const data = await authService.verifyToken();
    console.log('🔍 Dados recebidos do verifyToken:', data.user);
    localStorage.setItem('user', JSON.stringify(data.user));
    return data.user;
  } catch (error) {
    localStorage.removeItem('user');
    return rejectWithValue(error.message);
  }
});

/**
 * Thunk para renovar token.
 * @async
 * @function refreshTokenAsync
 * @returns {Promise<void>}
 */
export const refreshTokenAsync = createAsyncThunk('auth/refreshToken', async (_, { rejectWithValue }) => {
  try {
    await authService.refreshToken();
    return;
  } catch (error) {
    localStorage.removeItem('user');
    return rejectWithValue(error.message);
  }
});

/**
 * Thunk para solicitar reset de senha.
 * @async
 * @function forgotPasswordAsync
 * @param {string} email - Email do usuário
 * @returns {Promise<void>}
 */
export const forgotPasswordAsync = createAsyncThunk('auth/forgotPassword', async (email, { rejectWithValue }) => {
  try {
    const data = await authService.requestPasswordReset(email);
    return data.message;
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

/**
 * Thunk para atualizar perfil.
 * @async
 * @function updateProfileAsync
 * @param {Object} userData - Dados do usuário
 * @returns {Promise<void>}
 */
export const updateProfileAsync = createAsyncThunk('auth/updateProfile', async (userData, { rejectWithValue }) => {
  try {
    const data = await authService.updateProfile(userData);
    localStorage.setItem('user', JSON.stringify(data.user));
    return data.user;
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

/**
 * Thunk para logout do usuário com sincronização de dados.
 * @async
 * @function logoutAsync
 * @returns {Promise<void>}
 */
export const logoutAsync = createAsyncThunk('auth/logout', async (_, { rejectWithValue, dispatch, getState }) => {
  try {
    const state = getState();
    
    // Salvar dados apenas se usuário ainda autenticado
    if (state.auth.isAuthenticated) {
      try {
        // 2a) Atualizar carrinho da sessão ativa
        if (state.cart.items.length > 0) {
          await dispatch(saveCartAsync(state.cart.items)).unwrap();
        }
        
        // 2b) Atualizar favoritos da sessão ativa
        if (state.favorites.items.length > 0) {
          await dispatch(saveFavoritesAsync(state.favorites.items)).unwrap();
        }
      } catch (saveError) {
        // Ignorar erros de salvamento durante logout
        console.warn('Erro ao salvar dados durante logout:', saveError);
      }
    }
    
    // Fazer logout no backend
    try {
      await authService.logout();
    } catch (logoutError) {
      // Ignorar erros de logout do backend
      console.warn('Erro no logout do backend:', logoutError);
    }
    
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    
    // 2c) Limpar carrinho local após sair da sessão (sem sincronizar)
    dispatch(clearCart());
    
    // 2d) Limpar favoritos locais após sair da sessão (sem sincronizar)
    dispatch(clearFavorites());
    
    // 2e) Voltar para home page
    dispatch(showAllProducts());
    
    return;
  } catch (error) {
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    
    // Mesmo com erro, limpar dados locais
    dispatch(clearCart());
    dispatch(clearFavorites());
    dispatch(showAllProducts());
    
    return rejectWithValue(error.message);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: (() => {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;
      console.log('💾 Usuário carregado do localStorage:', user);
      return user;
    })(),
    isLoading: false,
    error: null,
    isAuthenticated: !!localStorage.getItem('user'),
    resetMessage: null,
    isLoggingOut: false
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    },
    clearError: (state) => {
      state.error = null;
    },
    clearResetMessage: (state) => {
      state.resetMessage = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        console.log('✅ Usuário autenticado no Redux:', action.payload);
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // SignUp
      .addCase(signUpAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signUpAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(signUpAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Verify Token
      .addCase(verifyTokenAsync.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        console.log('🔍 Token verificado, usuário carregado:', action.payload);
      })
      .addCase(verifyTokenAsync.rejected, (state) => {
        state.user = null;
        state.isAuthenticated = false;
      })
      // Refresh Token
      .addCase(refreshTokenAsync.fulfilled, (state) => {
        // Token renovado no cookie httpOnly
      })
      .addCase(refreshTokenAsync.rejected, (state) => {
        state.user = null;
        state.isAuthenticated = false;
      })
      // Logout
      .addCase(logoutAsync.pending, (state) => {
        state.isLoggingOut = true;
      })
      .addCase(logoutAsync.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isLoggingOut = false;
      })
      .addCase(logoutAsync.rejected, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isLoggingOut = false;
      })
      // Forgot Password
      .addCase(forgotPasswordAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgotPasswordAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.resetMessage = action.payload;
      })
      .addCase(forgotPasswordAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Profile
      .addCase(updateProfileAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfileAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
      })
      .addCase(updateProfileAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        // Se erro for 401, limpa sessão
        if (action.payload?.includes('401')) {
          state.user = null;
          state.isAuthenticated = false;
          localStorage.removeItem('user');
        }
      });
  }
});

export const { logout, clearError, clearResetMessage } = authSlice.actions;
export default authSlice.reducer;